<template>
    <LoadingPanel
        :headline="t('generators.portfolioOverview.groupStatusSummary.headline')"
        :is-pending="isPending"
        :is-error="isError"
    >
        <div
            v-if="data"
            class="group-summary"
        >
            <v-chart
                class="group-summary__chart"
                :option="option"
                autoresize
                @click="handleChartClick"
            />
        </div>
    </LoadingPanel>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { use } from 'echarts/core'
import { ECElementEvent, EChartsOption } from 'echarts'
import { BarChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'
import {
    TooltipComponent,
    LegendComponent,
    GridComponent,
    DatasetComponent,
    MarkLineComponent,
    MarkPointComponent,
    ToolboxComponent,
    AriaComponent,
    TitleComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import LoadingPanel from '@/components/panel/LoadingPanel.vue'
import { usePortfolioGroupSummaryQuery } from '@/api/queries/generators/portfolio-overview/usePortfolioGroupSummaryQuery'
import { ConditionStatus, SUMMARY_STATUS } from '@/configuration/status/condition-status'
import { CHART_ICON } from '@/utils/chart-utils'
import type { CallbackDataParams } from 'echarts/types/dist/shared'
import { ColorType } from '@/configuration/color/color-type'
import { useTheme } from '@/composables/useTheme'
import {
    getChartLabelBackgroundColor,
    getChartLabelColor,
    getChartToolboxInactiveColor,
} from '@/utils/echarts-theme'

use([
    TitleComponent,
    BarChart,
    CanvasRenderer,
    TooltipComponent,
    LegendComponent,
    GridComponent,
    DatasetComponent,
    MarkLineComponent,
    MarkPointComponent,
    ToolboxComponent,
    AriaComponent,
])

const emit = defineEmits<{
    'click:group': [value: number]
}>()

const { t } = useI18n()
const { isDarkMode } = useTheme()

const { data, isPending, isError } = usePortfolioGroupSummaryQuery()

const plantGroupSids = computed(() => {
    if (!data.value?.plantGroupSids) {
        return []
    }

    return data.value.plantGroupSids
})

const series = computed(() => [
    {
        ...SUMMARY_STATUS[ConditionStatus.Normal],
        data: data.value?.numberOfNormalComponents?.map((e: number) => e || '-') ?? [],
    },
    {
        ...SUMMARY_STATUS[ConditionStatus.Inferior],
        data: data.value?.numberOfInferiorComponents?.map((e: number) => e || '-') ?? [],
    },
    {
        ...SUMMARY_STATUS[ConditionStatus.Critical],
        data: data.value?.numberOfCriticalComponents?.map((e: number) => e || '-') ?? [],
    },
    {
        ...SUMMARY_STATUS[ConditionStatus.NoAction],
        data: data.value?.numberOfNoActionComponents?.map((e: number) => e || '-') ?? [],
    },
    {
        ...SUMMARY_STATUS[ConditionStatus.None],
        data: data.value?.numberOfNotAssessedComponents?.map((e: number) => e || '-') ?? [],
    },
])

const option = computed((): EChartsOption => {
    const options: EChartsOption = {
        title: {
            show: false,
            text: t('generators.portfolioOverview.groupStatusSummary.headline'),
        },
        toolbox: {
            show: true,
            itemSize: 16,
            itemGap: 14,
            showTitle: true,
            feature: {
                saveAsImage: {
                    icon: CHART_ICON.SAVE,
                    title: t('chart.action.saveAsImage'),
                },
            },
            iconStyle: {
                color: getChartToolboxInactiveColor(isDarkMode.value),
                borderColor: 'none',
            },
            emphasis: {
                iconStyle: {
                    color: ColorType.Primary,
                    borderColor: 'none',
                },
            },
        },
        legend: {},
        grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true,
        },
        xAxis: {
            type: 'value',
        },
        yAxis: {
            type: 'category',
            data: plantGroupSids.value,
            axisLabel: {
                formatter: (value, index) => data.value?.plantGroups?.[index] ?? '',
            },
        },
        aria: {
            enabled: true,
            decal: {
                show: true,
            },
        },
        series: [],
    }

    series.value.forEach((series) => {
        if (!Array.isArray(options.series)) {
            return
        }

        options.series.push({
            id: series.id,
            name: t(series.name),
            type: 'bar',
            stack: 'total',
            color: series.color,
            barWidth: '90%',
            barMaxWidth: 60,
            itemStyle: {
                decal: series.pattern,
            },
            label: {
                show: true,
                color: getChartLabelColor(isDarkMode.value),
                fontWeight: 'bold',
                backgroundColor: getChartLabelBackgroundColor(isDarkMode.value),
                formatter: (params: CallbackDataParams) => `${params.data}`,
            },
            data: series.data,
        })
    })

    return options
})

const handleChartClick = (event: ECElementEvent) => {
    const plantGroupSid = parseInt(event.name)
    emit('click:group', plantGroupSid)
}
</script>

<style scoped lang="scss">
.group-summary {
    &__chart {
        margin-top: 72px;
        width: 100%;
        height: 700px;
    }
}
</style>

<style lang="scss">
.container {
    &__name {
        font-size: $default-font-size;
        color: $pbl-text;
        font-weight: 500;
    }

    &__content {
        margin: 10px 0 0;

        &__item-div {
            margin: 10px 0 0;
            display: flex;
            gap: 16px;
            align-items: flex-start;

            &__color-div {
                display: flex;
                gap: 10px;
                align-items: center;

                &__color-circle {
                    display: inline-block;
                    border-radius: 10px;
                    width: 10px;
                    height: 10px;
                }

                &__series-name {
                    font-size: $default-font-size;
                    color: $pbl-text-muted;
                    font-weight: 400;
                    line-height: 18px;
                }
            }

            &__tooltip {
                font-size: $default-font-size;
                color: $pbl-text;
                font-weight: 500;
                line-height: 18px;
                max-width: 200px;
                white-space: normal;
            }
        }
    }
}
</style>
